import Button from 'devextreme-react/button';
import DataGrid, { Column, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import TextBox from 'devextreme-react/text-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { showConfirmDialog, showErrorToast, showSuccessToast } from '@jarvis/platform-ui';
import {
  fetchMediaCategories,
  fetchMediaGallery,
  softDeleteMediaAsset,
  type MediaCategoryChip,
  type MediaGalleryCard
} from '../../../api/mediaLibrary';
import { MediaLibraryAssetPopup } from '../../Apps/Utilities/MediaLibraryAssetPopup';
import { MediaLibraryIngestPopup } from '../../Apps/Utilities/MediaLibraryIngestPopup';
import '../../../apps/utilityApps.css';
import './gifLibraryAdmin.css';

const PAGE_SIZE = 50;

function PreviewCell(row: MediaGalleryCard) {
  const src = row.posterUrl ?? row.thumbnailUrl;
  return src ? (
    <img className="gif-library-admin__thumb" src={src} alt="" loading="lazy" decoding="async" />
  ) : (
    <span className="utility-app__muted">—</span>
  );
}

export function GifLibraryAdminPage() {
  const [items, setItems] = useState<MediaGalleryCard[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categories, setCategories] = useState<MediaCategoryChip[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ingestOpen, setIngestOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  const reloadCategories = useCallback(async () => {
    try {
      setCategories(await fetchMediaCategories('gifs'));
    } catch {
      /* grid reload surfaces errors */
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const page = await fetchMediaGallery({
        library: 'gifs',
        q: debouncedQ || undefined,
        categoryId,
        skip,
        take: PAGE_SIZE
      });
      setItems(page.items);
      setTotal(page.totalCount);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load GIF library.');
    } finally {
      setLoading(false);
    }
  }, [categoryId, debouncedQ, skip]);

  useEffect(() => {
    void reloadCategories();
  }, [reloadCategories]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const pageIndex = Math.floor(skip / PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const categoryChips = useMemo(
    () => [{ id: null as string | null, name: 'All' }, ...categories],
    [categories]
  );

  async function onDeactivate(row: MediaGalleryCard) {
    const confirmed = await showConfirmDialog({
      title: 'Deactivate GIF',
      message: `Remove “${row.name}” from the site GIF library? It will leave the gallery immediately and can be restored from Media Library recycle bin.`,
      confirmText: 'Deactivate',
      danger: true
    });
    if (!confirmed) return;

    setBusy(true);
    try {
      await softDeleteMediaAsset(row.id);
      showSuccessToast('GIF deactivated.');
      await reload();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not deactivate GIF.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ops-fill-page gif-library-admin">
      <div className="ops-fill-page__header">
        <div>
          <h1 className="utility-app__title" style={{ margin: 0 }}>
            GIF Library
          </h1>
          <p className="utility-app__muted" style={{ margin: '0.25rem 0 0' }}>
            Managed GIFs used around the site. Add or edit items here without reseeding.
          </p>
        </div>
        <div className="utility-app__toolbar-group">
          {loading || busy ? <LoadIndicator visible height={18} width={18} /> : null}
          <Button text="Add item" type="default" icon="plus" onClick={() => setIngestOpen(true)} />
          <Button text="Refresh" icon="refresh" stylingMode="outlined" onClick={() => void reload()} />
        </div>
      </div>

      <div className="ops-fill-page__panel gif-library-admin__panel">
        <div className="gif-library-admin__filters">
          <div className="emoji-kb__cats" role="tablist" aria-label="Filter by category">
            {categoryChips.map((chip) => {
              const selected = chip.id === categoryId;
              return (
                <Button
                  key={chip.id ?? 'all'}
                  text={chip.name}
                  stylingMode={selected ? 'contained' : 'outlined'}
                  type={selected ? 'default' : 'normal'}
                  onClick={() => {
                    setSkip(0);
                    setCategoryId(chip.id);
                  }}
                />
              );
            })}
          </div>
          <TextBox
            value={q}
            width="100%"
            mode="search"
            placeholder="Search name, tags…"
            showClearButton
            onValueChanged={(e) => {
              setSkip(0);
              setQ(String(e.value ?? ''));
            }}
          />
          <div className="gif-library-admin__pager">
            <span className="utility-app__muted">
              Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
            </span>
            <div className="gif-library-admin__pager-actions">
              <Button
                text="Previous"
                stylingMode="outlined"
                disabled={pageIndex <= 0 || loading}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              />
              <span className="utility-app__muted">
                Page {pageIndex + 1} / {pageCount}
              </span>
              <Button
                text="Next"
                stylingMode="outlined"
                disabled={pageIndex + 1 >= pageCount || loading}
                onClick={() => setSkip(skip + PAGE_SIZE)}
              />
            </div>
          </div>
        </div>

        <div className="ops-fill-page__grid-host">
          <DataGrid
            dataSource={items}
            keyExpr="id"
            showBorders
            rowAlternationEnabled
            hoverStateEnabled
            height="100%"
            columnAutoWidth
            wordWrapEnabled
            paging={{ enabled: false }}
            onRowDblClick={(e) => {
              if (e.data) setEditId((e.data as MediaGalleryCard).id);
            }}
          >
            <Scrolling mode="standard" showScrollbar="always" useNative />
            <SearchPanel visible={false} />
            <Column
              caption="Preview"
              width={80}
              allowSorting={false}
              cellRender={(cell) => <PreviewCell {...(cell.data as MediaGalleryCard)} />}
            />
            <Column dataField="name" caption="Name" minWidth={180} />
            <Column dataField="categoryName" caption="Category" width={160} />
            <Column
              caption="Tags"
              minWidth={160}
              allowSorting={false}
              calculateCellValue={(row: MediaGalleryCard) => (row.tags ?? []).join(', ')}
            />
            <Column
              caption="Actions"
              width={180}
              allowSorting={false}
              cellRender={(cell) => {
                const row = cell.data as MediaGalleryCard;
                return (
                  <div className="gif-library-admin__actions">
                    <Button text="Edit" stylingMode="text" onClick={() => setEditId(row.id)} />
                    <Button
                      text="Deactivate"
                      stylingMode="text"
                      type="danger"
                      onClick={() => void onDeactivate(row)}
                    />
                  </div>
                );
              }}
            />
          </DataGrid>
        </div>
      </div>

      <MediaLibraryIngestPopup
        open={ingestOpen}
        library="gifs"
        onClose={() => setIngestOpen(false)}
        onCompleted={() => {
          setIngestOpen(false);
          void reload();
        }}
      />
      <MediaLibraryAssetPopup
        assetId={editId}
        open={Boolean(editId)}
        canManage
        onClose={() => setEditId(null)}
        onChanged={() => void reload()}
      />
    </div>
  );
}
